import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack, Section } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { eq } from 'tio-ui/utilities';
import { formatCurrency, formatShortDate } from 'tio-ui/format';
import Button from 'tio-ui/components/button';
import PageHero from 'tio-ui/components/page-hero';
import { t } from 'ember-intl';
import { PhSparkle, PhArrowRight, PhTarget, PhBookOpen } from 'ember-phosphor-icons';
import StatePill from 'tio-employee/components/reimagined/tas/state-pill';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type {
  DataSourceInfo,
  LiveTasApplication,
  LiveTasData,
  LiveTasProgram,
} from 'tio-employee/services/reimagined-data';
import { isActiveApplicationState } from 'tio-common/utils/tuition-assistance/application-states';

const INACTIVE_PROGRAM_STATES = new Set([
  'TAS.ProgramInstanceState.WITHDRAWN',
  'TAS.ProgramInstanceState.WITHDRAWAL_APPROVED',
  'TAS.ProgramInstanceState.WITHDRAWAL_NOT_APPROVED',
  'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED',
  'TAS.ProgramInstanceState.ABANDONED',
  'TAS.ProgramInstanceState.COMPLETED',
  'TAS.ProgramInstanceState.COMPLETION_APPROVED',
  'TAS.ProgramInstanceState.COMPLETION_NOT_APPROVED',
  'TAS.ProgramInstanceState.COMPLETION_REJECTED',
  'TAS.ProgramInstanceState.PROGRAM_NOT_APPROVED',
  'TAS.ProgramInstanceState.PROGRAM_REJECTED',
]);

const PROGRAM_TYPES_IN_MOTION = new Set([
  'TAS.ProgramType.1',
  'TAS.ProgramType.4.2',
  'TAS.ProgramType.4.3',
]);

const RECENT_UPDATE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const PAID_APPLICATION_STATES = new Set([
  'TAS.ApplicationPaymentState.PAID',
  'TAS.ApplicationPaymentState.PAID_IN_FULL',
]);

const PENDING_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

const REQUESTED_APPLICATION_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
]);

interface TasIndexModel {
  tasData: LiveTasData;
  tasUsedAmount: { yearToDate: number; isLiveData: boolean };
  dataSourceInfo: DataSourceInfo;
}

interface TasIndexSignature {
  Args: {
    model: TasIndexModel;
  };
}

export default class CareerPathwaysTasIndex extends Component<TasIndexSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get tasSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.args.model.tasData.isLiveData ? 'live' : 'placeholder';
  }

  get programs(): LiveTasProgram[] {
    return this.args.model.tasData.programs;
  }

  get programsInMotion() {
    const cutoff = Date.now() - RECENT_UPDATE_WINDOW_MS;

    return this.programs.filter((program) => {
      const typeClassification = program.typeClassification || 'TAS.ProgramType.4.3';
      if (!PROGRAM_TYPES_IN_MOTION.has(typeClassification)) {
        return false;
      }

      if (!INACTIVE_PROGRAM_STATES.has(program.state)) {
        return true;
      }

      if (!program.updatedAt) {
        return false;
      }

      const updatedAt = Date.parse(program.updatedAt);
      if (Number.isNaN(updatedAt)) {
        return false;
      }

      return updatedAt >= cutoff;
    });
  }

  get applications(): LiveTasApplication[] {
    return this.args.model.tasData.applications;
  }

  applicationAmountInfo = (application: LiveTasApplication) => {
    if (application.recordType === 'instance') {
      return null;
    }
    const isRequestedState = REQUESTED_APPLICATION_STATES.has(application.state);
    const reimbursementApprovedTotal = isRequestedState
      ? application.approvedTotal
      : application.approvedTotal;

    const courseCashApprovedTotal = isRequestedState
      ? (application.courseCashPreApprovedTotal ?? application.courseCashFinalApprovedTotal ?? 0)
      : (application.courseCashFinalApprovedTotal ?? application.courseCashPreApprovedTotal ?? 0);

    const hasCourseCashUsage =
      application.courseCashUsed !== null || application.courseCashReserved !== null;
    const isCourseCashApp = courseCashApprovedTotal > 0 || hasCourseCashUsage;

    if (application.paymentState === 'TAS.ApplicationPaymentState.REFUND_REQUESTED') {
      const amount = application.approvedTotal;
      if (amount <= 0) {
        return null;
      }
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_repayment',
        courseCashUsed: application.courseCashUsed ?? 0,
        courseCashReserved: application.courseCashReserved ?? 0,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (
      PAID_APPLICATION_STATES.has(application.paymentState) ||
      application.paymentState === 'TAS.ApplicationPaymentState.PAID_IN_PART'
    ) {
      const amount = application.paidTotal || application.approvedTotal;
      if (amount <= 0) {
        return null;
      }
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_paid',
        courseCashUsed: application.courseCashUsed ?? 0,
        courseCashReserved: application.courseCashReserved ?? 0,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (PENDING_APPLICATION_STATES.has(application.state)) {
      const amount = reimbursementApprovedTotal;
      if (amount <= 0) {
        return null;
      }
      return {
        reimbursementAmount: amount,
        reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_pending',
        courseCashUsed: application.courseCashUsed ?? 0,
        courseCashReserved: application.courseCashReserved ?? 0,
        hasCourseCash: isCourseCashApp,
      };
    }

    if (reimbursementApprovedTotal <= 0) {
      return null;
    }

    return {
      reimbursementAmount: reimbursementApprovedTotal,
      reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_approved',
      courseCashUsed: application.courseCashUsed ?? 0,
      courseCashReserved: application.courseCashReserved ?? 0,
      hasCourseCash: isCourseCashApp,
    };
  };

  get applicationsInMotion() {
    const cutoff = Date.now() - RECENT_UPDATE_WINDOW_MS;

    return this.applications.filter((application) => {
      if (application.recordType === 'instance') {
        return false;
      }

      if (isActiveApplicationState(application.state)) {
        return true;
      }

      const isCompletedUnpaid =
        application.state === 'TAS.ApplicationState.FULFILLED' &&
        !PAID_APPLICATION_STATES.has(application.paymentState);

      if (isCompletedUnpaid) {
        return true;
      }

      const updatedAt = application.updatedAt || application.createdAt;
      const updatedAtValue = updatedAt ? Date.parse(updatedAt) : Number.NaN;

      if (Number.isNaN(updatedAtValue)) {
        return false;
      }

      return updatedAtValue >= cutoff;
    });
  }

  <template>
    <VStack class="gap-8">
      <PageHero @gradient="cool" @rounded="2xl" class="shadow-lg">
        <:default>
          <div class="tio-col-loose md:flex-row md:items-center md:justify-between">
            <div class="max-w-2xl">
              <div class="tio-row-tight tio-text-xs-semibold uppercase tracking-wide text-white/80">
                <PhSparkle class="tio-icon-sm" />
                {{t "tuition_assistance.reimagined.index.hero_eyebrow"}}
              </div>
              <Heading @level={{1}} class="tio-fg-inverse">
                {{t "tuition_assistance.reimagined.index.hero_title"}}
              </Heading>
              <Text class="text-white/80">
                {{t "tuition_assistance.reimagined.index.hero_subtitle"}}
              </Text>
            </div>
            <div class="tio-wrap">
              <LinkTo @route="authenticated.tas.dashboard">
                <Button @intent="primary">{{t
                    "tuition_assistance.reimagined.index.cta_start"
                  }}</Button>
              </LinkTo>
              <LinkTo @route="authenticated.reimagined.tas.programs">
                <Button
                  @intent="primary"
                  @appearance="outlined"
                  class="border-white/50 tio-fg-inverse bg-white/15 hover:bg-white/25"
                >
                  {{t "tuition_assistance.reimagined.index.cta_explore"}}
                </Button>
              </LinkTo>
            </div>
          </div>
        </:default>
      </PageHero>

      <DataIndicator @source={{this.tasSource}} @showBadge={{true}} @dataSourceId="tas-overview">
        <Section>
          <:header>
            <div class="tio-row-between">
              <Heading @level={{2}}>{{t
                  "tuition_assistance.reimagined.index.section_overview"
                }}</Heading>
              <LinkTo @route="authenticated.tas.dashboard" class="tio-copy tio-text-sm-semibold">
                {{t "tuition_assistance.reimagined.index.link_view_dashboard"}}
              </LinkTo>
            </div>
          </:header>
          <:body>
            <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div class="rounded-2xl ring-1 ring-gray-950/[0.04] tio-bg-surface p-5 shadow-sm">
                <div class="tio-row-between">
                  <Text @variant="muted">{{t
                      "tuition_assistance.reimagined.index.card_approved"
                    }}</Text>
                  <PhTarget class="tio-icon-md tio-fg-info" />
                </div>
                <Heading @level={{3}}>
                  {{formatCurrency @model.tasData.summary.totalApproved}}
                </Heading>
              </div>
              <div class="rounded-2xl ring-1 ring-gray-950/[0.04] tio-bg-surface p-5 shadow-sm">
                <div class="tio-row-between">
                  <Text @variant="muted">{{t
                      "tuition_assistance.reimagined.index.card_pending"
                    }}</Text>
                  <PhBookOpen class="tio-icon-md tio-fg-warning" />
                </div>
                <Heading @level={{3}}>
                  {{@model.tasData.summary.pendingCount}}
                </Heading>
              </div>
              <div class="rounded-2xl ring-1 ring-gray-950/[0.04] tio-bg-surface p-5 shadow-sm">
                <div class="tio-row-between">
                  <Text @variant="muted">{{t
                      "tuition_assistance.reimagined.index.card_used"
                    }}</Text>
                  <PhSparkle class="tio-icon-md tio-fg-brand" />
                </div>
                <Heading @level={{3}}>
                  {{formatCurrency @model.tasUsedAmount.yearToDate}}
                </Heading>
              </div>
            </div>
          </:body>
        </Section>
      </DataIndicator>

      <DataIndicator @source={{this.tasSource}} @showBadge={{true}} @dataSourceId="tas-programs">
        <Section>
          <:header>
            <div class="tio-row-between">
              <Heading @level={{2}}>{{t
                  "tuition_assistance.reimagined.index.section_programs"
                }}</Heading>
              <LinkTo
                @route="authenticated.reimagined.tas.programs"
                class="tio-copy tio-text-sm-semibold"
              >
                {{t "tuition_assistance.reimagined.index.link_view_programs"}}
              </LinkTo>
            </div>
          </:header>
          <:body>
            {{#if this.programsInMotion.length}}
              <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {{#each this.programsInMotion as |program|}}
                  <div class="rounded-2xl ring-1 ring-gray-950/[0.04] tio-bg-surface p-5 shadow-sm">
                    <div class="tio-row-start-between gap-4">
                      <div>
                        <Heading @level={{3}}>{{program.name}}</Heading>
                        <Text @variant="muted">{{program.institution}}</Text>
                        {{#if program.startDate}}
                          <Text @variant="muted" class="block whitespace-nowrap">
                            {{t
                              "tuition_assistance.reimagined.index.label_starts"
                              date=(formatShortDate program.startDate)
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <StatePill @state={{program.state}} />
                    </div>
                    <div class="mt-4">
                      <LinkTo
                        @route="authenticated.tas.programs.show"
                        @model={{program.id}}
                        class="tio-copy tio-text-sm-semibold"
                      >
                        {{t "tuition_assistance.reimagined.index.link_open_program"}}
                        <PhArrowRight class="tio-icon-sm ml-1 inline" />
                      </LinkTo>
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div class="rounded-2xl border border-dashed tio-border-strong tio-bg-sunken p-8">
                <Heading @level={{3}}>{{t
                    "tuition_assistance.reimagined.index.empty_programs_title"
                  }}</Heading>
                <Text @variant="muted">
                  {{t "tuition_assistance.reimagined.index.empty_programs_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">{{t
                        "tuition_assistance.reimagined.index.empty_programs_cta"
                      }}</Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>
      </DataIndicator>

      <DataIndicator
        @source={{this.tasSource}}
        @showBadge={{true}}
        @dataSourceId="tas-applications"
      >
        <Section>
          <:header>
            <div class="tio-row-between">
              <Heading @level={{2}}>{{t
                  "tuition_assistance.reimagined.index.section_applications"
                }}</Heading>
              <LinkTo
                @route="authenticated.reimagined.tas.applications"
                class="tio-copy tio-text-sm-semibold"
              >
                {{t "tuition_assistance.reimagined.index.link_view_applications"}}
              </LinkTo>
            </div>
          </:header>
          <:body>
            {{#if this.applicationsInMotion.length}}
              <div class="grid grid-cols-1 gap-4">
                {{#each this.applicationsInMotion as |application|}}
                  <div class="rounded-2xl ring-1 ring-gray-950/[0.04] tio-bg-surface p-5 shadow-sm">
                    <div class="tio-row-start-between gap-4">
                      <div>
                        <Heading @level={{3}}>{{application.name}}</Heading>
                        <Text @variant="muted">{{application.programName}}</Text>
                        {{#if application.coursesBeginDate}}
                          <Text @variant="muted" class="block whitespace-nowrap">
                            {{t
                              "tuition_assistance.reimagined.index.label_starts"
                              date=(formatShortDate application.coursesBeginDate)
                            }}
                          </Text>
                        {{/if}}
                      </div>
                      <div class="tio-row-tight">
                        <StatePill @state={{application.state}} />
                        <StatePill @state={{application.paymentState}} />
                      </div>
                    </div>
                    <div class="mt-4 tio-row-loose">
                      {{#let (this.applicationAmountInfo application) as |amountInfo|}}
                        {{#if amountInfo}}
                          <div class="tio-row-tight text-xs tio-fg-disabled">
                            <span class="tio-text-sm-semibold tio-fg-primary">
                              {{formatCurrency amountInfo.reimbursementAmount}}
                            </span>
                            <span>{{t amountInfo.reimbursementLabelKey}}</span>
                            {{#if amountInfo.hasCourseCash}}
                              <span>•</span>
                              <span class="tio-text-sm-semibold tio-fg-primary">
                                {{formatCurrency amountInfo.courseCashUsed}}
                              </span>
                              <span>{{t
                                  "tuition_assistance.reimagined.index.application_amount_used"
                                }}</span>
                              <span>•</span>
                              <span class="tio-text-sm-semibold tio-fg-primary">
                                {{formatCurrency amountInfo.courseCashReserved}}
                              </span>
                              <span>{{t
                                  "tuition_assistance.reimagined.index.application_amount_remaining"
                                }}</span>
                            {{/if}}
                          </div>
                        {{/if}}
                      {{/let}}
                      {{#if (eq application.recordType "application")}}
                        <LinkTo
                          @route="authenticated.tas.applications.show"
                          @model={{application.id}}
                          class="tio-copy tio-text-sm-semibold"
                        >
                          {{t "tuition_assistance.reimagined.index.link_review"}}
                          <PhArrowRight class="tio-icon-sm ml-1 inline" />
                        </LinkTo>
                      {{else}}
                        <LinkTo
                          @route="authenticated.reimagined.tas.programs.show"
                          @model={{application.id}}
                          class="tio-copy tio-text-sm-semibold"
                        >
                          {{t "tuition_assistance.reimagined.index.link_open_program"}}
                          <PhArrowRight class="tio-icon-sm ml-1 inline" />
                        </LinkTo>
                      {{/if}}
                    </div>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div class="rounded-2xl border border-dashed tio-border-strong tio-bg-sunken p-8">
                <Heading @level={{3}}>{{t
                    "tuition_assistance.reimagined.index.empty_applications_title"
                  }}</Heading>
                <Text @variant="muted">
                  {{t "tuition_assistance.reimagined.index.empty_applications_body"}}
                </Text>
                <div class="mt-4">
                  <LinkTo @route="authenticated.tas.dashboard">
                    <Button @intent="primary">{{t
                        "tuition_assistance.reimagined.index.empty_applications_cta"
                      }}</Button>
                  </LinkTo>
                </div>
              </div>
            {{/if}}
          </:body>
        </Section>
      </DataIndicator>
    </VStack>
  </template>
}
